import React, { useMemo, useState } from 'react';
import type { TipoArredondamento, TipoFinais } from '../../../../../@types/IPA/PrecoPsicologico';
import { simularPrecoLocal } from '../../../../../services/roundingEngine';

interface Props {
  tipo: TipoFinais;
  finaisPermitidos: string[];
  opcaoArredondamento: TipoArredondamento;
  valorDe?: number;
  valorAte?: number;
}

function formatarResultado(valor: number, tipo: TipoFinais): { prefixo: string; destaque: string } {
  const formatado = valor.toFixed(2).replace('.', ',');
  // tipo 1→1 char, tipo 2→2 chars, tipo 3→4 chars (X,XX), tipo 4→5 (XX,XX), tipo 5→6 (XXX,XX)
  const tamDestaque = Math.min(tipo <= 2 ? tipo : tipo + 1, formatado.length);
  const prefixo = formatado.slice(0, formatado.length - tamDestaque);
  const destaque = formatado.slice(formatado.length - tamDestaque);
  return { prefixo: `R$ ${prefixo}`, destaque };
}

export function SimuladorDePreco({ tipo, finaisPermitidos, opcaoArredondamento, valorDe, valorAte }: Props) {
  const [valorTeste, setValorTeste] = useState(0);

  const resultado = useMemo(() => {
    if (valorTeste <= 0 || finaisPermitidos.filter(Boolean).length === 0) return null;
    return simularPrecoLocal(valorTeste, finaisPermitidos.filter(Boolean), tipo, opcaoArredondamento);
  }, [valorTeste, finaisPermitidos, tipo, opcaoArredondamento]);

  const foraFaixa = useMemo(() => {
    if (valorTeste <= 0) return false;
    if (!valorDe && !valorAte) return false;
    if (valorDe === 0 && valorAte === 0) return false;
    return valorTeste < (valorDe ?? 0) || valorTeste > (valorAte ?? Infinity);
  }, [valorTeste, valorDe, valorAte]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value.replace(/\D/g, '');
    const num = parseInt(raw, 10) || 0;
    setValorTeste(num / 100);
  }

  const mascara = valorTeste > 0
    ? valorTeste.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

  const desabilitado = finaisPermitidos.filter(Boolean).length === 0;

  return (
    <div className="simulador">
      <div className="simulador__header">
        <span className="simulador__titulo">Teste seu preço</span>
        <span className="simulador__subtitulo">
          Simule para ver como fica o preço com esse arredondamento
        </span>
      </div>
      <div className="simulador__linha">
        <div className="simulador__campo">
          <div className="simulador__input-group">
            <span className="simulador__addon">R$</span>
            <input
              type="text"
              className="simulador__input"
              value={mascara}
              onChange={handleChange}
              placeholder="00,00"
              disabled={desabilitado}
            />
          </div>
        </div>

        {foraFaixa ? (
          <span className="simulador__fora-faixa">
            Preço fora da faixa — a regra não seria aplicada a esse valor.
          </span>
        ) : (
          resultado && (
            <>
              <svg className="simulador__seta" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
              <div className="simulador__resultado">
                {(() => {
                  const { prefixo, destaque } = formatarResultado(resultado.precoSimulado, tipo);
                  return (
                    <>
                      <span>{prefixo}</span>
                      <span className="simulador__resultado-destaque">{destaque}</span>
                    </>
                  );
                })()}
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}
